import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as TodoIcon,
  PlayArrow as InProgressIcon,
  DeleteForever as DeleteForeverIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";
import MDButton from "../MDButton";

// API services
import { tasksAPI } from "../../services/api";

const TodoList = () => {
  const currentUser = useSelector((state) => state.auth.user);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    assignee_id: null,
    mentioned_user_ids: [],
  });
  const [expandedRows, setExpandedRows] = useState(new Set());

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchTasks = async (force = false) => {
    const cachedTasksString = localStorage.getItem("tasks");
    const cachedTimestamp = localStorage.getItem("tasks_timestamp");
    let isCacheValid = false;

    if (!force && cachedTasksString) {
      try {
        const tasksData = JSON.parse(cachedTasksString);
        setTasks(tasksData);
        setLoading(false);

        if (
          cachedTimestamp &&
          Date.now() - parseInt(cachedTimestamp) < CACHE_DURATION
        ) {
          isCacheValid = true;
        }
      } catch (e) {
        console.error(
          "Fehler beim Parsen der zwischengespeicherten Aufgaben",
          e,
        );
      }
    }

    if (!force && isCacheValid) {
      return;
    }

    if (force) {
      setLoading(true);
    }

    try {
      const response = await tasksAPI.getTasks();
      if (response.data) {
        setTasks(response.data);
        localStorage.setItem("tasks", JSON.stringify(response.data));
        localStorage.setItem("tasks_timestamp", Date.now().toString());
      }
    } catch (err) {
      console.error("Fehler beim Laden der Aufgaben:", err);
      if (force) {
        setError("Fehler beim Aktualisieren der Aufgaben.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    const cachedUsersString = localStorage.getItem("users");
    const cachedTimestamp = localStorage.getItem("users_timestamp");
    let isCacheValid = false;

    if (cachedUsersString) {
      try {
        const usersData = JSON.parse(cachedUsersString);
        setUsers(usersData);

        if (
          cachedTimestamp &&
          Date.now() - parseInt(cachedTimestamp) < CACHE_DURATION
        ) {
          isCacheValid = true;
        }
      } catch (e) {
        console.error(
          "Fehler beim Parsen der zwischengespeicherten Benutzer",
          e,
        );
      }
    }

    if (isCacheValid) {
      return;
    }

    try {
      const response = await tasksAPI.getUsers();
      setUsers(response.data);
      localStorage.setItem("users", JSON.stringify(response.data));
      localStorage.setItem("users_timestamp", Date.now().toString());
    } catch (err) {
      console.error("Fehler beim Laden der Benutzer:", err);
    }
  };

  const handleCreateTask = async () => {
    try {
      const response = await tasksAPI.createTask(formData);
      setTasks([...tasks, response.data]);
      handleCloseDialog();
    } catch (err) {
      console.error("Error creating task:", err);
      setError("Error creating task. Please try again.");
    }
  };

  const handleUpdateTask = async () => {
    try {
      const response = await tasksAPI.updateTask(editTask.id, formData);
      setTasks(
        tasks.map((task) => (task.id === editTask.id ? response.data : task)),
      );
      handleCloseDialog();
    } catch (err) {
      console.error("Error updating task:", err);
      setError("Error updating task. Please try again.");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksAPI.deleteTask(taskId);
      setTasks(tasks.filter((task) => task.id !== taskId));
    } catch (err) {
      console.error("Error deleting task:", err);
      setError("Error deleting task. Please try again.");
    }
  };

  const handleClearArchivedTasks = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete ALL archived tasks? This cannot be undone.",
      )
    ) {
      return;
    }
    try {
      await tasksAPI.clearArchivedTasks();
      fetchTasks();
    } catch (err) {
      console.error("Error clearing archived tasks:", err);
      setError("Error clearing archived tasks. Please try again.");
    }
  };

  const handleDeleteArchivedTask = async (taskId) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this archived task?",
      )
    ) {
      return;
    }
    try {
      await tasksAPI.deleteArchivedTask(taskId);
      setTasks(tasks.filter((task) => task.id !== taskId));
    } catch (err) {
      console.error("Error deleting archived task:", err);
      setError("Error deleting archived task. Please try again.");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "todo":
        return <TodoIcon color="action" />;
      case "in_progress":
        return <InProgressIcon color="warning" />;
      case "done":
        return <CheckCircleIcon color="success" />;
      default:
        return <TodoIcon color="action" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "todo":
        return "To Do";
      case "in_progress":
        return "In Progress";
      case "done":
        return "Done";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "todo":
        return "default";
      case "in_progress":
        return "warning";
      case "done":
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "low":
        return "Low";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      case "urgent":
        return "Urgent";
      default:
        return priority;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "info";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "urgent":
        return "error";
      default:
        return "default";
    }
  };

  const handleOpenDialog = (task = null) => {
    setEditTask(task);
    setFormData(
      task
        ? {
            title: task.title || "",
            description: task.description || "",
            status: task.status || "todo",
            priority: task.priority || "medium",
            assignee_id: task.assignee?.id || null,
            mentioned_user_ids:
              task.mentioned_users?.map((user) => user.id) || [],
          }
        : {
            title: "",
            description: "",
            status: "todo",
            priority: "medium",
            assignee_id: currentUser?.id || null,
            mentioned_user_ids: [],
          },
    );
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditTask(null);
    setFormData({
      title: "",
      description: "",
      status: "todo",
      priority: "medium",
      assignee_id: null,
      mentioned_user_ids: [],
    });
  };

  const handleSaveTask = () => {
    if (editTask) {
      handleUpdateTask();
    } else {
      handleCreateTask();
    }
  };

  const todoTasks = tasks.filter((t) => t.status === "todo");
  const inProgressTasks = tasks.filter((t) => t.status === "in_progress");
  const doneTasks = tasks.filter((t) => t.status === "done");

  const renderTaskRow = (task) => (
    <React.Fragment key={task.id}>
      <TableRow
        sx={{
          cursor: "pointer",
          backgroundColor: task.is_archived
            ? "rgba(255, 255, 0, 0.1)"
            : "inherit",
          "&:hover": {
            backgroundColor: task.is_archived
              ? "rgba(255, 255, 0, 0.2)"
              : "rgba(0, 123, 255, 0.4)",
          },
          "&:last-child td, &:last-child th": { border: 0 },
        }}
        onClick={() => {
          const newExpanded = new Set(expandedRows);
          if (newExpanded.has(task.id)) {
            newExpanded.delete(task.id);
          } else {
            newExpanded.add(task.id);
          }
          setExpandedRows(newExpanded);
        }}
      >
        <TableCell component="th" scope="row">
          <Box display="flex" alignItems="center" gap={1}>
            {task.is_archived && (
              <Chip
                label="Archived"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            <Box>
              <Typography variant="subtitle2" fontWeight="medium">
                {task.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {task.description.length > 100
                  ? task.description.substring(0, 100) + "..."
                  : task.description}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={getPriorityLabel(task.priority)}
            color={getPriorityColor(task.priority)}
            size="small"
          />
        </TableCell>
        <TableCell>
          {task.assignee ? (
            <Typography variant="body2">
              {task.assignee.full_name || task.assignee.username}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Not assigned
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {new Date(task.created_at).toLocaleDateString("de-DE")}
        </TableCell>
        <TableCell align="right">
          {task.is_archived && currentUser?.is_superuser && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArchivedTask(task.id);
              }}
              color="error"
              title="Delete Permanently"
            >
              <DeleteForeverIcon />
            </IconButton>
          )}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(task);
            }}
            color="info"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTask(task.id);
            }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {expandedRows.has(task.id) && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{
              backgroundColor: task.is_archived
                ? "rgba(255, 255, 0, 0.05)"
                : "rgba(0, 123, 255, 0.15)",
              padding: 2,
            }}
          >
            <Typography variant="body2">{task.description}</Typography>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );

  return (
    <MDBox>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <MDTypography variant="h4" fontWeight="medium">
            Tasks
          </MDTypography>
          <Tooltip title="Refresh Tasks">
            <span>
              <IconButton
                onClick={() => fetchTasks(true)}
                disabled={loading}
                size="small"
              >
                <RefreshIcon
                  sx={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
        <Box display="flex" gap={2}>
          {currentUser?.is_superuser && (
            <MDButton
              variant="outlined"
              color="error"
              size="large"
              startIcon={<DeleteForeverIcon />}
              onClick={handleClearArchivedTasks}
              sx={{
                px: 3,
                py: 2.5,
                fontSize: "1rem",
              }}
            >
              Clear Archived
            </MDButton>
          )}
          <MDButton
            variant="gradient"
            color="info"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              px: 5,
              py: 2.5,
              fontSize: "1.3rem",
              minWidth: "200px",
              pr: 6,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.08)",
                boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            Add New Task
          </MDButton>
        </Box>
      </MDBox>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tasks table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Title</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Assigned To</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Created At</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading tasks...
                </TableCell>
              </TableRow>
            ) : (
              <>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 0, 0, 0.4)",
                      textAlign: "left",
                      fontSize: "1.2rem",
                    }}
                  >
                    To Do ({todoTasks.length})
                  </TableCell>
                </TableRow>
                {todoTasks.map(renderTaskRow)}
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 255, 0, 0.4)",
                      textAlign: "left",
                      fontSize: "1.2rem",
                    }}
                  >
                    In Progress ({inProgressTasks.length})
                  </TableCell>
                </TableRow>
                {inProgressTasks.map(renderTaskRow)}
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "rgba(0, 255, 0, 0.4)",
                      textAlign: "left",
                      fontSize: "1.2rem",
                    }}
                  >
                    Done ({doneTasks.length})
                  </TableCell>
                </TableRow>
                {doneTasks.map(renderTaskRow)}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Task Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        maxHeight="lg"
      >
        <DialogTitle>
          {editTask ? "Aufgabe bearbeiten" : "Neue Aufgabe erstellen"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={10}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={formData.status}
                    label="State"
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <MenuItem value="todo">To Do</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="done">Done</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) =>
                      setFormData({ ...formData, priority: e.target.value })
                    }
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="large">
                  <InputLabel>Assigned to</InputLabel>
                  <Select
                    value={formData.assignee_id || ""}
                    label="Assigned to"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        assignee_id: e.target.value || null,
                      })
                    }
                    sx={{
                      minHeight: "60px",
                      fontSize: "1.1rem",
                      "& .MuiSelect-select": {
                        paddingLeft: "20px",
                        paddingRight: "40px",
                        minWidth: "200px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "2px",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>No Assignment</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.full_name || user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={users}
                  getOptionLabel={(option) =>
                    option.full_name || option.username
                  }
                  value={users.filter((user) =>
                    formData.mentioned_user_ids.includes(user.id),
                  )}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      mentioned_user_ids: newValue.map((user) => user.id),
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Mention Users"
                      placeholder="Select users"
                      size="large"
                      sx={{
                        "& .MuiInputBase-root": {
                          minHeight: "60px",
                          fontSize: "1.1rem",
                          paddingLeft: "20px",
                          paddingRight: "40px",
                          minWidth: "200px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: "2px",
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiChip-root": {
                      fontSize: "1rem",
                      height: "36px",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveTask}
            variant="contained"
            disabled={!formData.title.trim()}
          >
            {editTask ? "update" : "create"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
};

export default TodoList;
